import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
} from '@mui/material';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import SearchableSelect from 'shared/components/SearchableSelect';
import { toast } from 'react-hot-toast';
import { fetchActiveTenantUsers } from 'shared/api/services/tenantUserService';
import { getRegistryTenantIdFromSession } from 'shared/utils/sessionProfileStore';
import { softwareApi } from '../api/systemRegisterApi';
import {
  CHANGE_PRIORITY,
  CHANGE_RISK_LEVEL,
  CHANGE_TYPE,
  IMPLEMENTATION_STATUS,
  formatEnumLabel,
} from '../constants/enums';
import { apiErrorMessage, unwrapRecords } from '../utils/responseHelpers';
import FormSection from './FormSection';

const userFilter = createFilterOptions({
  stringify: (option) =>
    `${option.name ?? ''} ${option.email ?? ''} ${option.id ?? ''}`.toLowerCase(),
});

const softwareFilter = createFilterOptions({
  stringify: (option) =>
    `${option.label ?? ''} ${option.softwareName ?? ''} ${option.id ?? ''}`.toLowerCase(),
});

const softwareLabel = (s) => {
  if (!s) return '';
  return (s.label || s.softwareName || '').trim() || `Software #${s.id}`;
};

const userLabel = (u) => {
  if (!u) return '';
  const name = (u.name || '').trim();
  const email = (u.email || '').trim();
  if (name && email) return `${name} (${email})`;
  return name || email || '';
};

const findUserOption = (users, value) => {
  const raw = (value || '').trim();
  if (!raw) return null;
  const byEmail = users.find((u) => (u.email || '').trim().toLowerCase() === raw.toLowerCase());
  if (byEmail) return byEmail;
  const byName = users.find((u) => (u.name || '').trim().toLowerCase() === raw.toLowerCase());
  if (byName) return byName;
  const byLabel = users.find((u) => userLabel(u).toLowerCase() === raw.toLowerCase());
  if (byLabel) return byLabel;
  return { id: `legacy-${raw}`, name: raw, email: '' };
};

const nowDatetimeLocal = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const emptyChangeManagementRow = (defaults = {}) => ({
  key: `chg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  id: null,
  softwareId: defaults.softwareId ?? null,
  title: '',
  description: '',
  changeType: '',
  priority: '',
  riskLevel: '',
  reasonForChange: '',
  requestedBy: defaults.requestedBy || '',
  requestedDate: defaults.requestedDate || nowDatetimeLocal(),
  currentVersion: '',
  targetVersion: '',
  implementationPlan: '',
  rollbackPlan: '',
  testPlan: '',
  testResult: '',
  scheduledStart: '',
  scheduledEnd: '',
  actualStart: '',
  actualEnd: '',
  downtimeRequired: false,
  downtimeDuration: '',
  implementationStatus: 'PLANNED',
  implementedBy: '',
  approvals: [],
  createdAt: '',
  updatedAt: '',
});

/**
 * Add / edit change management dialog.
 * When `selectSoftware` is true, user must pick a software system first.
 */
export default function ChangeManagementDialog({
  open,
  mode,
  value,
  saving = false,
  onClose,
  onSave,
  selectSoftware = false,
  softwareOptions: softwareOptionsProp = null,
}) {
  const [draft, setDraft] = useState(emptyChangeManagementRow());
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [softwareOptions, setSoftwareOptions] = useState([]);
  const [loadingSoftware, setLoadingSoftware] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDraft(value ? { ...emptyChangeManagementRow(), ...value } : emptyChangeManagementRow());
  }, [open, value]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoadingUsers(true);
    const tenantId = getRegistryTenantIdFromSession();
    fetchActiveTenantUsers(tenantId)
      .then((list) => {
        if (!cancelled) setUsers(Array.isArray(list) ? list : []);
      })
      .catch((err) => {
        const msg = err?.response?.data?.message || err?.message || 'Failed to load users';
        toast.error(typeof msg === 'string' ? msg : 'Failed to load users');
        if (!cancelled) setUsers([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingUsers(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open || !selectSoftware) {
      setSoftwareOptions([]);
      return undefined;
    }
    if (Array.isArray(softwareOptionsProp) && softwareOptionsProp.length) {
      setSoftwareOptions(softwareOptionsProp);
      return undefined;
    }
    let cancelled = false;
    setLoadingSoftware(true);
    softwareApi
      .list({ page: 0, size: 500, status: 'ACTIVE' })
      .then((response) => {
        if (cancelled) return;
        const { rows } = unwrapRecords(response);
        setSoftwareOptions(
          (rows || []).map((s) => ({
            id: s.id,
            label: s.softwareName || `Software #${s.id}`,
            softwareName: s.softwareName,
          }))
        );
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(apiErrorMessage(err, 'Failed to load software register.'));
          setSoftwareOptions([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingSoftware(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, selectSoftware, softwareOptionsProp]);

  useEffect(() => {
    if (!selectSoftware || !Array.isArray(softwareOptionsProp)) return;
    setSoftwareOptions(softwareOptionsProp);
  }, [selectSoftware, softwareOptionsProp]);

  const setField = useCallback((name, val) => {
    setDraft((prev) => ({ ...prev, [name]: val }));
  }, []);

  const requestedByOption = useMemo(
    () => findUserOption(users, draft.requestedBy),
    [users, draft.requestedBy]
  );
  const implementedByOption = useMemo(
    () => findUserOption(users, draft.implementedBy),
    [users, draft.implementedBy]
  );
  const selectedSoftware = useMemo(() => {
    if (draft.softwareId == null) return null;
    return (
      softwareOptions.find((s) => String(s.id) === String(draft.softwareId))
      || { id: draft.softwareId, label: `Software #${draft.softwareId}` }
    );
  }, [softwareOptions, draft.softwareId]);

  const handleSave = async () => {
    if (selectSoftware && draft.softwareId == null) {
      toast.error('Select a software system to link this change.');
      return;
    }
    if (!(draft.title || '').trim()) {
      toast.error('Title is required.');
      return;
    }
    if (saving) return;
    await onSave?.({
      ...draft,
      softwareId: draft.softwareId ?? null,
      title: draft.title.trim(),
      downtimeDuration:
        draft.downtimeDuration === '' || draft.downtimeDuration == null
          ? ''
          : Number(draft.downtimeDuration),
    });
  };

  const enumField = (name, label, options) => (
    <SearchableSelect
      size="medium"
      label={label}
      value={draft[name] || ''}
      onChange={(next) => setField(name, next)}
      options={options.map((opt) => ({ value: opt, label: formatEnumLabel(opt) }))}
    />
  );

  const textField = (name, label, props = {}) => (
    <TextField
      fullWidth
      size="small"
      label={label}
      value={draft[name] ?? ''}
      onChange={(e) => setField(name, e.target.value)}
      {...props}
    />
  );

  const userField = (name, label, option) => (
    <Autocomplete
      fullWidth
      options={users}
      loading={loadingUsers}
      filterOptions={userFilter}
      getOptionLabel={(opt) => userLabel(opt)}
      isOptionEqualToValue={(a, b) => a?.id === b?.id}
      value={option}
      onChange={(_, opt) => setField(name, opt?.email || opt?.name || '')}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          size="medium"
          label={label}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loadingUsers ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{mode === 'edit' ? 'Edit change' : 'Add change'}</DialogTitle>
      <DialogContent dividers>
        {selectSoftware ? (
          <FormSection title="Software">
            <Grid container spacing={1.5} sx={{ width: '100%' }}>
              <Grid size={12}>
                <Autocomplete
                  size="small"
                  options={softwareOptions}
                  loading={loadingSoftware}
                  filterOptions={softwareFilter}
                  value={selectedSoftware}
                  onChange={(_, option) => setField('softwareId', option?.id ?? null)}
                  getOptionLabel={(option) => softwareLabel(option)}
                  isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      label="Software"
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingSoftware ? <CircularProgress color="inherit" size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </FormSection>
        ) : null}
        <FormSection title="Change details">
          <Grid container spacing={1.5} sx={{ width: '100%' }}>
            <Grid size={12}>
              {textField('title', 'Title', { required: true })}
            </Grid>
            <Grid size={12}>
              {textField('description', 'Description', { multiline: true, minRows: 2 })}
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              {enumField('changeType', 'Change type', CHANGE_TYPE)}
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              {enumField('priority', 'Priority', CHANGE_PRIORITY)}
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              {enumField('riskLevel', 'Risk level', CHANGE_RISK_LEVEL)}
            </Grid>
            <Grid size={12}>
              {textField('reasonForChange', 'Reason for change', { multiline: true, minRows: 2 })}
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              {userField('requestedBy', 'Requested by', requestedByOption)}
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              {textField('requestedDate', 'Requested date', { type: 'datetime-local', InputLabelProps: { shrink: true } })}
            </Grid>
          </Grid>
        </FormSection>

        <FormSection title="Versions">
          <Grid container spacing={1.5} sx={{ width: '100%' }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              {textField('currentVersion', 'Current version')}
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              {textField('targetVersion', 'Target version')}
            </Grid>
          </Grid>
        </FormSection>

        <FormSection title="Plans & testing">
          <Grid container spacing={1.5} sx={{ width: '100%' }}>
            <Grid size={12}>
              {textField('implementationPlan', 'Implementation plan', { multiline: true, minRows: 2 })}
            </Grid>
            <Grid size={12}>
              {textField('rollbackPlan', 'Rollback plan', { multiline: true, minRows: 2 })}
            </Grid>
            <Grid size={12}>
              {textField('testPlan', 'Test plan', { multiline: true, minRows: 2 })}
            </Grid>
            <Grid size={12}>
              {textField('testResult', 'Test result', { multiline: true, minRows: 2 })}
            </Grid>
          </Grid>
        </FormSection>

        <FormSection title="Schedule & downtime">
          <Grid container spacing={1.5} sx={{ width: '100%' }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              {textField('scheduledStart', 'Scheduled start', { type: 'datetime-local', InputLabelProps: { shrink: true } })}
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              {textField('scheduledEnd', 'Scheduled end', { type: 'datetime-local', InputLabelProps: { shrink: true } })}
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              {textField('actualStart', 'Actual start', { type: 'datetime-local', InputLabelProps: { shrink: true } })}
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              {textField('actualEnd', 'Actual end', { type: 'datetime-local', InputLabelProps: { shrink: true } })}
            </Grid>
            <Grid size={12}>
              <SearchableSelect
                size="medium"
                label="Downtime required"
                allowEmpty={false}
                value={draft.downtimeRequired ? 'YES' : 'NO'}
                onChange={(next) => setField('downtimeRequired', next === 'YES')}
                options={[
                  { value: 'NO', label: 'No' },
                  { value: 'YES', label: 'Yes' },
                ]}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              {textField('downtimeDuration', 'Downtime duration (minutes)', {
                type: 'number',
                disabled: !draft.downtimeRequired,
              })}
            </Grid>
          </Grid>
        </FormSection>

        <FormSection title="Implementation">
          <Grid container spacing={1.5} sx={{ width: '100%' }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              {enumField('implementationStatus', 'Implementation status', IMPLEMENTATION_STATUS)}
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              {userField('implementedBy', 'Implemented by', implementedByOption)}
            </Grid>
          </Grid>
        </FormSection>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleSave}
          disabled={saving}
          sx={{ textTransform: 'none' }}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
